<div class="dashboard-container">
  <!-- Header -->
  <app-staff-header 
    [currentUser]="currentUser" 
    (logout)="onLogout()">
  </app-staff-header>

  <!-- Main Content -->
  <div class="dashboard-content">
    <!-- Navigation -->
    <app-staff-nav 
      [isMobileMenuOpen]="isMobileMenuOpen"
      (navigate)="onNavigate($event)"
      (closeMobileMenu)="closeMobileMenu()">
    </app-staff-nav>

    <!-- Content Area -->
    <div class="dashboard-main">
      <div class="main-content">
        <!-- Page Header -->
        <div class="content-header">
          <h1 class="page-title">จัดการหน้าที่</h1>
          <p class="page-subtitle">ระบบจัดการบทบาทและสิทธิ์การใช้งาน</p>
        </div>

        <!-- Mock Data Warning -->
        <ngb-alert 
          *ngIf="showMockDataWarning" 
          type="info" 
          [dismissible]="false"
          class="mb-3">
          <strong>ข้อมูลตัวอย่าง:</strong> 
          ระบบกำลังใช้ข้อมูลตัวอย่างเนื่องจากไม่สามารถเชื่อมต่อ backend API ได้
        </ngb-alert>

        <!-- Error Alert -->
        <ngb-alert 
          *ngIf="error" 
          type="danger" 
          [dismissible]="true"
          class="mb-3">
          {{ error }}
        </ngb-alert>

        <!-- Filters Section -->
        <div class="filters-section">
          <div class="filters-header">
            <h3>ตัวกรอง</h3>
            <button 
              type="button" 
              class="clear-filters-btn" 
              (click)="clearFilters()">
              ล้างตัวกรอง
            </button>
          </div>
          
          <div class="filters-grid">
            <!-- Search Term -->
            <div class="filter-group">
              <label class="form-label">ค้นหาทั่วไป</label>
              <input 
                type="text" 
                class="form-control" 
                [(ngModel)]="filters.searchTerm" 
                (ngModelChange)="applyFilters()"
                placeholder="ชื่อบทบาท, รหัส หรือคำอธิบาย">
            </div>

            <!-- Role Code Search -->
            <div class="filter-group">
              <label class="form-label">รหัสบทบาท</label>
              <input 
                type="text" 
                class="form-control" 
                [(ngModel)]="filters.roleCode" 
                (ngModelChange)="applyFilters()"
                placeholder="รหัสบทบาท">
            </div>

            <!-- Status -->
            <div class="filter-group">
              <label class="form-label">สถานะ</label>
              <select 
                class="form-select" 
                [(ngModel)]="filters.status" 
                (ngModelChange)="applyFilters()">
                <option value="all">ทั้งหมด</option>
                <option value="active">ใช้งาน</option>
                <option value="inactive">ไม่ใช้งาน</option>
              </select>
            </div>

            <!-- Has Users -->
            <div class="filter-group">
              <label class="form-label">การใช้งาน</label>
              <select 
                class="form-select" 
                [(ngModel)]="filters.hasUsers" 
                (ngModelChange)="applyFilters()">
                <option value="all">ทั้งหมด</option>
                <option value="with-users">มีผู้ใช้งาน</option>
                <option value="without-users">ไม่มีผู้ใช้งาน</option>
              </select>
            </div>
          </div>
          
          <!-- Action Buttons -->
          <div class="d-flex gap-2 mt-3">
            <button 
              type="button" 
              class="btn btn-success" 
              (click)="createNewRole()">
              <i class="bi bi-plus-circle me-2"></i>สร้างบทบาทใหม่
            </button>
            <button 
              type="button" 
              class="btn btn-outline-success" 
              (click)="exportToCSV()"
              [disabled]="isExporting || filteredRoles.length === 0">
              <span *ngIf="isExporting" class="spinner-border spinner-border-sm me-2"></span>
              <i *ngIf="!isExporting" class="bi bi-file-earmark-excel me-2"></i>
              {{ isExporting ? 'กำลัง Export...' : 'Export CSV' }}
            </button>
            <button 
              type="button" 
              class="btn btn-primary" 
              (click)="refreshData()">
              <i class="bi bi-arrow-clockwise me-2"></i>รีเฟรชข้อมูล
            </button>
          </div>
        </div>

        <!-- Loading Spinner -->
        <div *ngIf="isLoading" class="role-management-container">
          <div class="loading-state">
            <div class="loading-spinner"></div>
            <p>กำลังโหลดข้อมูลบทบาท...</p>
          </div>
        </div>

        <!-- Roles Table -->
        <div class="card" *ngIf="!isLoading">
          <div class="card-header d-flex justify-content-between align-items-center">
            <h5 class="card-title mb-0">รายการบทบาท</h5>
            <div class="d-flex align-items-center gap-2">
              <span class="text-muted">แสดงผล:</span>
              <select class="form-select form-select-sm w-auto" [(ngModel)]="pageSize"
                  (ngModelChange)="changePageSize(pageSize)" aria-label="จำนวนรายการต่อหน้า">
                  <option *ngFor="let size of pageSizeOptions" [ngValue]="size">{{ size }} รายการ</option>
                </select>
            </div>
          </div>

          <div class="card-body p-0">
            <!-- Table -->
            <div class="table-responsive">
              <table class="table table-striped table-hover mb-0">
                <thead class="table-light">
                  <tr>
                    <th scope="col" class="sortable" (click)="sortBy('roleName')">
                      ชื่อบทบาท {{ getSortIcon('roleName') }}
                    </th>
                    <th scope="col" class="sortable" (click)="sortBy('roleCode')">
                      รหัสบทบาท {{ getSortIcon('roleCode') }}
                    </th>
                    <th scope="col">คำอธิบาย</th>
                    <th scope="col" class="text-center">สิทธิ์การใช้งาน</th>
                    <th scope="col" class="text-center sortable" (click)="sortBy('userCount')">
                      จำนวนผู้ใช้งาน {{ getSortIcon('userCount') }}
                    </th>
                    <th scope="col" class="sortable" (click)="sortBy('createdAt')">
                      วันที่สร้าง {{ getSortIcon('createdAt') }}
                    </th>
                    <th scope="col" class="sortable" (click)="sortBy('updatedAt')">
                      แก้ไขล่าสุด {{ getSortIcon('updatedAt') }}
                    </th>
                    <th scope="col" class="text-center">สถานะ</th>
                    <th scope="col" class="text-center">จัดการ</th>
                  </tr>
                </thead>
                <tbody>
                  <!-- No Data Row -->
                  <tr *ngIf="!isLoading && getCurrentPageRoles().length === 0">
                    <td colspan="9" class="text-center py-4">
                      <div class="text-muted">
                        <i class="bi bi-inbox"></i>
                        <p class="mb-0">ไม่พบข้อมูลบทบาท</p>
                        <small *ngIf="roles.length === 0">ไม่มีข้อมูลในระบบ</small>
                        <small *ngIf="roles.length > 0 && filteredRoles.length === 0">ไม่พบข้อมูลที่ตรงกับเงื่อนไขการกรอง</small>
                      </div>
                    </td>
                  </tr>

                  <!-- Role Rows -->
                  <tr *ngFor="let role of getCurrentPageRoles(); trackBy: trackByRoleId" 
                      class="clickable-row" 
                      title="คลิกเพื่อดูรายละเอียดบทบาท">
                    <td>
                      <div class="d-flex align-items-center">
                        <div class="flex-grow-1">
                          <h6 class="mb-0">{{ role.roleName }}</h6>
                          <small class="text-muted">{{ role.createdBy }}</small>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span class="badge bg-primary">{{ role.roleCode }}</span>
                    </td>
                    <td>
                      <span [ngbTooltip]="role.description" class="description-text">
                        {{ role.description }}
                      </span>
                    </td>
                    <td class="text-center">
                      <button 
                        type="button" 
                        class="btn btn-sm btn-outline-info"
                        (click)="viewRolePermissions(role)"
                        [ngbTooltip]="'ดูสิทธิ์การใช้งาน'">
                        <i class="bi bi-shield-check me-1"></i>{{ role.permissions.length }} สิทธิ์
                      </button>
                    </td>
                    <td class="text-center">
                      <button 
                        type="button" 
                        class="btn btn-sm"
                        [class.btn-outline-success]="role.userCount > 0"
                        [class.btn-outline-secondary]="role.userCount === 0"
                        (click)="manageRoleUsers(role)"
                        [ngbTooltip]="role.userCount > 0 ? 'จัดการผู้ใช้งาน' : 'ไม่มีผู้ใช้งาน'">
                        <i class="bi bi-people me-1"></i>{{ role.userCount }}
                      </button>
                    </td>
                    <td>
                      <span class="text-muted">{{ formatDate(role.createdAt) }}</span>
                    </td>
                    <td>
                      <span class="text-muted">{{ formatDate(role.updatedAt) }}</span>
                    </td>
                    <td class="text-center">
                      <span 
                        class="badge"
                        [class.bg-success]="role.isActive"
                        [class.bg-secondary]="!role.isActive">
                        {{ role.isActive ? 'ใช้งาน' : 'ไม่ใช้งาน' }}
                      </span>
                    </td>
                    <td class="text-center">
                      <div ngbDropdown 
                           class="d-inline-block" 
                           (click)="$event.stopPropagation()"
                           (openChange)="onDropdownOpenChange($event, role.id)"
                           placement="bottom-right">
                        <button 
                          class="btn btn-sm btn-outline-primary dropdown-toggle" 
                          type="button" 
                          id="dropdownMenuButton-{{role.id}}"
                          ngbDropdownToggle>
                          <i class="bi bi-gear me-1"></i>จัดการ
                        </button>
                        <div class="dropdown-menu dropdown-menu-end" 
                             ngbDropdownMenu 
                             [attr.aria-labelledby]="'dropdownMenuButton-' + role.id">
                          <button 
                            class="dropdown-item"
                            type="button"
                            (click)="editRole(role)">
                            <i class="bi bi-pencil-square me-2"></i>แก้ไขข้อมูล
                          </button>
                          <button 
                            class="dropdown-item"
                            type="button"
                            (click)="viewRolePermissions(role)">
                            <i class="bi bi-shield-check me-2"></i>ดูสิทธิ์การใช้งาน
                          </button>
                          <button 
                            class="dropdown-item"
                            type="button"
                            (click)="manageRoleUsers(role)">
                            <i class="bi bi-people me-2"></i>จัดการผู้ใช้งาน
                          </button>
                          <div class="dropdown-divider"></div>
                          <button 
                            class="dropdown-item"
                            type="button"
                            [class.text-success]="!role.isActive"
                            [class.text-warning]="role.isActive"
                            (click)="toggleRoleStatus(role)">
                            <i class="bi" 
                               [class.bi-check-circle]="!role.isActive"
                               [class.bi-pause-circle]="role.isActive"
                               me-2></i>
                            {{ role.isActive ? 'ปิดใช้งาน' : 'เปิดใช้งาน' }}
                          </button>
                          <div class="dropdown-divider"></div>
                          <button 
                            class="dropdown-item text-danger"
                            type="button"
                            (click)="deleteRole(role)"
                            [disabled]="role.userCount > 0">
                            <i class="bi bi-trash me-2"></i>ลบบทบาท
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Pagination -->
          <div class="card-footer">
            <div class="d-flex justify-content-between align-items-center">
              <div class="text-muted">
                แสดงผล {{ ((page - 1) * pageSize) + 1 }} - {{ min(page * pageSize, collectionSize) }} 
                จาก {{ collectionSize }} รายการ
              </div>
              <ngb-pagination 
                [(page)]="page" 
                [pageSize]="pageSize" 
                [collectionSize]="collectionSize"
                [maxSize]="5"
                [rotate]="true"
                [boundaryLinks]="true"
                [size]="'sm'">
              </ngb-pagination>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
